import { Box, Container, styled } from '@mui/material';
import { getTodayDateString } from 'components/inputs/dateHelpers';
import React, { useState } from 'react';
import { PayComponent } from 'routes/HumanResourcesModule/PayState/helpers/constants';
import HypothesisSelector from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/AddHypotheses/AgentHypothesisModal/HypothesisSelector';
import {
    HrSimulation,
    SimulationDateString,
} from 'routes/HumanResourcesModule/Simulation/helpers/types';
import MultiStepModal, { ModalStepT } from 'ui/Modals/MultiStepModal';
import PaymentsIcon from '@mui/icons-material/Payments';
import ApplyConditionsStep from './components/ApplyConditionsStep';
import IfseInformationStep from './components/IfseInformationStep';
import {
    FreeHypothesisMutationParams,
    OperationType,
    ValueType,
} from './helpers/types';
import { useTranslation } from 'react-i18next';

type FreeHypothesisCreationModalProps = {
    show: boolean;
    onClose: () => void;
    createFreeHypothesis: (payload: FreeHypothesisMutationParams) => void;
    hrSimulation: HrSimulation;
    hasChange: () => void;
};
const StyBox = styled(Box)`
    height: '10rem';
`;
const StyContainer = styled(Container)`
    paddingX: '1rem 16rem',
    display: 'flex',
    alignItems: 'center ',
    height: '100% !important',
    justifyContent: 'center',`;
enum HypothesisChoice {
    IFSE = PayComponent.IFSE_AND_MONTHLY_PRIMES,
}
export default function FreeHypothesisCreationModal({
    show,
    onClose,
    createFreeHypothesis,
    hrSimulation,
    hasChange,
}: FreeHypothesisCreationModalProps): React.ReactElement {
    const { t } = useTranslation();
    const [newValue, setNewValue] = useState<number | undefined>(0);
    const [operationType, setOperationType] = useState<OperationType>(
        OperationType.INCREASE,
    );
    const [valueType, setValueType] = useState<ValueType>(ValueType.PERCENT);
    const [isFirstStep, setIsFirstStep] = useState(true);
    const [selectedHypothesisType, setSelectedHypothesisType] =
        useState<HypothesisChoice>(HypothesisChoice.IFSE);
    const [category, setCategory] = useState<string>('');
    const [cadreEmploi, setCadreEmploi] = useState('');
    const [filiere, setFiliere] = useState('');
    const [jobs, setJobs] = useState<string[]>([]);
    const [minValue, setMinValue] = useState<number | undefined>(0);
    const [maxValue, setMaxValue] = useState<number | undefined>(0);
    const [agentsNumber, setAgentsNumber] = useState<number>(0);

    const [applicationDate, setApplicationDate] =
        useState<SimulationDateString>(
            getTodayDateString() as SimulationDateString,
        );

    const isValid = Boolean((maxValue ?? 0) > (minValue ?? 0));

    const onClickSave = () => {
        createFreeHypothesis({
            operation: operationType,
            valueType,
            newValue: newValue ?? 0,
            filters: {
                category,
                cadreEmploi,
                filiere,
                jobLabel: jobs,
                greaterThan: minValue,
                lowerThan: maxValue,
                date: applicationDate,
            },
            agentsNumber,
        });
        hasChange();
        onClose();
    };
    const firstStep = {
        confirmAction: {
            enabled: selectedHypothesisType !== null,
            onClick: () => {
                setIsFirstStep(false);
            },
        },
        title: t('humanResources.simulation.freeHypothesis.selectHypothesis'),
        node: (
            <StyContainer>
                <HypothesisSelector
                    selected={selectedHypothesisType === HypothesisChoice.IFSE}
                    onClick={() => {
                        setSelectedHypothesisType(HypothesisChoice.IFSE);
                    }}
                    Icon={PaymentsIcon}
                    title={PayComponent.IFSE_AND_MONTHLY_PRIMES}
                    style={{ width: '100%' }}
                />
            </StyContainer>
        ),
    };

    const secondStep = {
        title: t(
            'humanResources.simulation.freeHypothesis.hypothesisType_one',
            {
                type: selectedHypothesisType,
            },
        ),
        node: (
            <StyBox>
                <IfseInformationStep
                    hrSimulationId={hrSimulation.id}
                    category={category}
                    setCategory={setCategory}
                    cadreEmploi={cadreEmploi}
                    setCadreEmploi={setCadreEmploi}
                    filiere={filiere}
                    setFiliere={setFiliere}
                    jobs={jobs}
                    setJobs={setJobs}
                    minValue={minValue}
                    setMinValue={setMinValue}
                    maxValue={maxValue}
                    setMaxValue={setMaxValue}
                />
            </StyBox>
        ),
        stepBackAction: {
            onClick: () => setIsFirstStep(true),
        },
        confirmAction: {
            enabled: isValid,
        },
    };

    const thirdStep = {
        title: t(
            'humanResources.simulation.freeHypothesis.hypothesisType_one',
            {
                type: selectedHypothesisType,
            },
        ),
        node: (
            <StyBox>
                <ApplyConditionsStep
                    hrSimulation={hrSimulation}
                    agentsNumber={agentsNumber}
                    setAgentsNumber={setAgentsNumber}
                    operationType={operationType}
                    setOperationType={setOperationType}
                    valueType={valueType}
                    setValueType={setValueType}
                    category={category}
                    cadreEmploi={cadreEmploi}
                    filiere={filiere}
                    minValue={minValue}
                    maxValue={maxValue}
                    applicationDate={applicationDate}
                    setApplicationDate={setApplicationDate}
                    setNewValue={setNewValue}
                    job={jobs}
                    newValue={0}
                />
            </StyBox>
        ),
        confirmAction: {
            label: t('common.add'),
            onClick: onClickSave,
        },
    };

    const steps: ModalStepT[] = [firstStep, secondStep, thirdStep];

    return (
        <MultiStepModal
            isOpen={show}
            onClose={onClose}
            isSmallHeight
            maxWidth="md"
            steps={steps}
            PaperProps={{ sx: { width: isFirstStep ? '30rem' : '50rem' } }}
        />
    );
}
